/**
 * Per-site consent rules, driven by the `implicitConsentEnabled` and `profilingConsentEnabled`
 * site preferences.
 */
export type ConsentPolicy = {
    /** Privacy and marketing are implicitly accepted: signup forms show the texts without checkboxes. */
    implicitConsent: boolean;
    /** Profiling consent and favourite interests exist on this site. */
    profilingEnabled: boolean;
};

export const DEFAULT_CONSENT_POLICY: ConsentPolicy = { implicitConsent: false, profilingEnabled: true };

export function toConsentPolicy(prefs: Partial<SitePreferencesCustomAttributes>): ConsentPolicy {
    return {
        implicitConsent: prefs.implicitConsentEnabled === true,
        profilingEnabled: prefs.profilingConsentEnabled !== false,
    };
}

type SignupConsents = {
    consentPrivacy?: boolean;
    consentMarketing?: boolean;
    consentProfiling?: boolean;
};

/**
 * Forces the consents the customer cannot choose on signup forms (registration, newsletter,
 * complete profile). Not meant for account preferences, where marketing can always be revoked.
 */
export function applySignupConsentPolicy<T extends SignupConsents>(values: T, policy: ConsentPolicy): T {
    return {
        ...values,
        ...(policy.implicitConsent && 'consentPrivacy' in values && { consentPrivacy: true }),
        ...(policy.implicitConsent && 'consentMarketing' in values && { consentMarketing: true }),
        ...(!policy.profilingEnabled && 'consentProfiling' in values && { consentProfiling: false }),
    };
}
